// Custody Calendar Utility Functions
import { RRule, RRuleSet } from 'rrule';
import {
  WEEKDAY_MAP,
  RRULE_WEEKDAY_MAP,
  WEEKDAYS,
  dateToString,
  formatCustodyDate,
  getOrdinalSuffix,
} from '../config/custody';
import type {
  CustodyState,
  CustodyPlan,
  CustodyDate,
  Household,
  Weekday,
  WeekSplit,
  HouseholdAssignment,
} from '../types/custody';

// Re-export config helpers so web pages can import from utils/custody
export { formatCustodyDate, dateToString, getOrdinalSuffix } from '../config/custody';

/**
 * Get weekend days as RRule weekday constants
 */
export const getWeekendDays = (weekendStart: Weekday, weekendEnd: Weekday): typeof RRule.SU[] => {
  const weekendDays: typeof RRule.SU[] = [];
  const wes = WEEKDAY_MAP[weekendStart];
  const wee = WEEKDAY_MAP[weekendEnd];

  if (wes < wee) {
    for (let w = wes; w < wee; w++) {
      weekendDays.push(RRULE_WEEKDAY_MAP[WEEKDAYS[w].key]);
    }
  } else {
    // Weekend spans across week boundary (e.g., Friday to Monday)
    for (let w = wes; w < 7; w++) {
      weekendDays.push(RRULE_WEEKDAY_MAP[WEEKDAYS[w].key]);
    }
    for (let w = 0; w < wee; w++) {
      weekendDays.push(RRULE_WEEKDAY_MAP[WEEKDAYS[w].key]);
    }
  }

  return weekendDays;
};

/**
 * Get weekend days as short codes (SU, MO, etc.)
 */
export const getWeekendDayCodes = (weekendStart: Weekday, weekendEnd: Weekday): string[] => {
  const weekendDays: string[] = [];
  const wes = WEEKDAY_MAP[weekendStart];
  const wee = WEEKDAY_MAP[weekendEnd];

  if (wes < wee) {
    for (let w = wes; w < wee; w++) {
      weekendDays.push(WEEKDAYS[w].short);
    }
  } else {
    for (let w = wes; w < 7; w++) {
      weekendDays.push(WEEKDAYS[w].short);
    }
    for (let w = 0; w < wee; w++) {
      weekendDays.push(WEEKDAYS[w].short);
    }
  }

  return weekendDays;
};

/**
 * Generate dates for day-based schemes (every other day, every X days)
 */
const getDayDates = (
  ruleSet: RRuleSet,
  interval: number,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  ruleSet.rrule(
    new RRule({
      freq: RRule.DAILY,
      interval: 2 * interval,
      dtstart: startDate,
      until: endDate,
    })
  );

  // Add offset rules for multi-day intervals
  for (let i = 1; i < interval; i++) {
    const offsetStart = new Date(startDate);
    offsetStart.setDate(offsetStart.getDate() + i * 7);

    ruleSet.rrule(
      new RRule({
        freq: RRule.DAILY,
        interval: 2 * interval,
        dtstart: offsetStart,
        until: endDate,
      })
    );
  }

  return ruleSet;
};

/**
 * Generate dates for week-based schemes
 */
const getWeekDates = (
  ruleSet: RRuleSet,
  interval: number,
  swapDayOfWeek: Weekday,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 2 * interval,
      wkst: RRULE_WEEKDAY_MAP[swapDayOfWeek],
      byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU],
      dtstart: startDate,
      until: endDate,
    })
  );

  for (let i = 1; i < interval; i++) {
    const offsetStart = new Date(startDate);
    offsetStart.setDate(offsetStart.getDate() + i * 7);

    ruleSet.rrule(
      new RRule({
        freq: RRule.WEEKLY,
        interval: 2 * interval,
        wkst: RRULE_WEEKDAY_MAP[swapDayOfWeek],
        byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU],
        dtstart: offsetStart,
        until: endDate,
      })
    );
  }

  return ruleSet;
};

/**
 * Generate dates for weekend-based schemes
 */
const getWeekendDates = (
  ruleSet: RRuleSet,
  interval: number,
  weekendStart: Weekday,
  weekendEnd: Weekday,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  const weekendDays = getWeekendDays(weekendStart, weekendEnd);

  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 2 * interval,
      wkst: RRULE_WEEKDAY_MAP[weekendStart],
      byweekday: weekendDays,
      dtstart: startDate,
      until: endDate,
    })
  );

  for (let i = 1; i < interval; i++) {
    const offsetStart = new Date(startDate);
    offsetStart.setDate(offsetStart.getDate() + i * 7);

    ruleSet.rrule(
      new RRule({
        freq: RRule.WEEKLY,
        interval: 2 * interval,
        wkst: RRULE_WEEKDAY_MAP[weekendStart],
        byweekday: weekendDays,
        dtstart: offsetStart,
        until: endDate,
      })
    );
  }

  return ruleSet;
};

/**
 * Generate dates for weekend/weekday schemes (all weekends)
 */
const getWeekendWeekDates = (
  ruleSet: RRuleSet,
  weekendStart: Weekday,
  weekendEnd: Weekday,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  const weekendDays = getWeekendDays(weekendStart, weekendEnd);

  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 1,
      wkst: RRULE_WEEKDAY_MAP[weekendStart],
      byweekday: weekendDays,
      dtstart: startDate,
      until: endDate,
    })
  );

  return ruleSet;
};

/**
 * Generate dates for weekend + split week scheme
 */
const getWeekendSplitDates = (
  ruleSet: RRuleSet,
  startHousehold: string,
  weekendStart: Weekday,
  weekendEnd: Weekday,
  splits: WeekSplit,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  const weekendDays = getWeekendDays(weekendStart, weekendEnd);

  // Add alternating weekends
  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 2,
      wkst: RRULE_WEEKDAY_MAP[weekendStart],
      byweekday: weekendDays,
      dtstart: startDate,
      until: endDate,
    })
  );

  // Add split week days for the start household
  const splitDays: typeof RRule.SU[] = [];
  Object.entries(splits).forEach(([day, assignment]) => {
    if (assignment?.householdId === startHousehold) {
      const rruleDay = RRule[day.toUpperCase() as keyof typeof RRule] as typeof RRule.SU;
      if (rruleDay) splitDays.push(rruleDay);
    }
  });

  if (splitDays.length > 0) {
    ruleSet.rrule(
      new RRule({
        freq: RRule.WEEKLY,
        interval: 1,
        wkst: RRULE_WEEKDAY_MAP[weekendStart],
        byweekday: splitDays,
        dtstart: startDate,
        until: endDate,
      })
    );
  }

  return ruleSet;
};

/**
 * Generate dates for 2-2-3 scheme.
 * 14-day cycle:
 *   Week 1: A=Mon-Tue, B=Wed-Thu, A=Fri-Sat-Sun
 *   Week 2: B=Mon-Tue, A=Wed-Thu, B=Fri-Sat-Sun
 * The startHousehold is "A" in the pattern above.
 */
const get223Dates = (
  ruleSet: RRuleSet,
  startHouseholdId: string,
  startDate: Date,
  endDate: Date
): RRuleSet => {
  // Week 1 days for startHousehold: Mon, Tue, Fri, Sat, Sun
  const week1Days = [RRule.MO, RRule.TU, RRule.FR, RRule.SA, RRule.SU];
  // Week 2 days for startHousehold: Wed, Thu
  const week2Days = [RRule.WE, RRule.TH];

  // Week 1: every 2 weeks starting from startDate
  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 2,
      wkst: RRule.MO,
      byweekday: week1Days,
      dtstart: startDate,
      until: endDate,
    })
  );

  // Week 2: every 2 weeks starting 1 week after startDate
  const week2Start = new Date(startDate);
  week2Start.setDate(week2Start.getDate() + 7);
  ruleSet.rrule(
    new RRule({
      freq: RRule.WEEKLY,
      interval: 2,
      wkst: RRule.MO,
      byweekday: week2Days,
      dtstart: week2Start,
      until: endDate,
    })
  );

  return ruleSet;
};

/**
 * Generate dates for alternating split scheme
 */
const getAlternatingDates = (
  ruleSet: RRuleSet,
  householdId: string,
  splits: WeekSplit[],
  startDate: Date,
  endDate: Date
): RRuleSet => {
  let weeks = 0;

  splits.forEach((split) => {
    const splitDays: typeof RRule.SU[] = [];

    Object.entries(split).forEach(([day, assignment]) => {
      if (assignment?.householdId === householdId) {
        const rruleDay = RRule[day.toUpperCase() as keyof typeof RRule] as typeof RRule.SU;
        if (rruleDay) splitDays.push(rruleDay);
      }
    });

    if (splitDays.length > 0) {
      const offsetStart = new Date(startDate);
      const daysUntilSunday = 7 - startDate.getDay();
      offsetStart.setDate(offsetStart.getDate() + weeks * daysUntilSunday);

      ruleSet.rrule(
        new RRule({
          freq: RRule.WEEKLY,
          interval: splits.length,
          wkst: RRule.SU,
          byweekday: splitDays,
          dtstart: offsetStart,
          until: endDate,
        })
      );
    }

    weeks++;
  });

  return ruleSet;
};

/**
 * Generate rule description text
 */
export const getRuleDescription = (
  state: CustodyState,
  households: Record<string, Household>
): { isParametersComplete: boolean; description: string } => {
  const hhKeys = Object.keys(households);
  if (hhKeys.length < 2) {
    return { isParametersComplete: false, description: 'Need at least two households' };
  }

  const hhOne = households[hhKeys[0]].name;
  const hhTwo = households[hhKeys[1]].name;
  const otherHouseholdName = hhOne === state.startHouseholdName ? hhTwo : hhOne;

  const startDateStr = formatCustodyDate(state.startDate);
  const endDateStr = formatCustodyDate(state.endDate);

  if (!state.custodyScheme) {
    return { isParametersComplete: false, description: 'Please select a custody scheme.' };
  }

  if (state.custodyScheme !== 'alternating' && !state.startHousehold) {
    return { isParametersComplete: false, description: 'Please specify which household has custody first.' };
  }

  let description = '';

  switch (state.custodyScheme) {
    case 'day':
      description = `Starting on ${startDateStr}, custody will alternate every other day starting at ${state.startHouseholdName}. This plan ends on ${endDateStr}.`;
      break;

    case 'xdays':
      description = `Starting on ${startDateStr}, custody will alternate every ${state.xDays} days starting at ${state.startHouseholdName}. This plan ends on ${endDateStr}.`;
      break;

    case 'week':
      description = `Starting on ${startDateStr}, custody will change every week starting at ${state.startHouseholdName}. Household changes will happen on ${state.swapDayOfWeek}. This plan ends on ${endDateStr}.`;
      break;

    case 'xweeks':
      description = `Starting on ${startDateStr}, custody will change every ${state.xWeeks} weeks starting at ${state.startHouseholdName}. Household changes will happen on ${state.swapDayOfWeek}. This plan ends on ${endDateStr}.`;
      break;

    case 'weekends':
      description = `Starting on ${startDateStr}, custody will change every other weekend starting at ${state.startHouseholdName}. Weekends start on ${state.weekendStart} and end on ${state.weekendEnd}. This plan ends on ${endDateStr}.`;
      break;

    case 'xweekends':
      description = `Starting on ${startDateStr}, custody will change every ${state.xWeekends} weekends starting at ${state.startHouseholdName}. Weekends start on ${state.weekendStart} and end on ${state.weekendEnd}. This plan ends on ${endDateStr}.`;
      break;

    case 'weekend_weekdays':
      description = `Starting on ${startDateStr}, the kids will be at ${state.startHouseholdName} on the weekends and at ${otherHouseholdName} during the week. Weekends start on ${state.weekendStart} and end on ${state.weekendEnd}. This plan ends on ${endDateStr}.`;
      break;

    case 'weekend_splitweek':
      if (state.splits.length === 0) {
        return { isParametersComplete: false, description: 'Please specify which days of the week each household has custody.' };
      }
      description = `Starting on ${startDateStr}, custody will change every other weekend with the first weekend at ${state.startHouseholdName}. Weekend household changes will happen on ${state.weekendStart} and ${state.weekendEnd}. This plan ends on ${endDateStr}.`;
      break;

    case 'alternating':
      if (state.splits.length < 2) {
        return { isParametersComplete: false, description: 'Please configure the alternating split pattern.' };
      }
      description = `Starting on ${startDateStr}, custody will alternate based on a custom split pattern. This plan ends on ${endDateStr}.`;
      break;

    case '223':
      description = `Starting on ${startDateStr}, custody follows a 2-2-3 schedule. Week 1: ${state.startHouseholdName} has Mon-Tue, ${otherHouseholdName} has Wed-Thu, ${state.startHouseholdName} has Fri-Sun. Week 2: reversed. Equal time over each 2-week cycle. This plan ends on ${endDateStr}.`;
      break;

    default:
      return { isParametersComplete: false, description: 'Unknown custody scheme.' };
  }

  return { isParametersComplete: true, description };
};

/**
 * Calculate all custody dates for a given state
 */
export const getCustodyDates = (
  state: CustodyState,
  households: Record<string, Household>,
  existingItems: Record<string, CustodyDate> = {}
): { items: Record<string, CustodyDate>; isParametersComplete: boolean; description: string } => {
  const ruleInfo = getRuleDescription(state, households);

  if (!ruleInfo.isParametersComplete) {
    return { items: existingItems, ...ruleInfo };
  }

  const hhKeys = Object.keys(households);
  const household1Id = hhKeys[0];
  const startDate = new Date(state.startDate);
  const endDate = new Date(state.endDate);

  // Create RRuleSet for first household's dates
  let house1Rules = new RRuleSet();

  switch (state.custodyScheme) {
    case 'day':
      house1Rules = getDayDates(house1Rules, 1, startDate, endDate);
      break;
    case 'xdays':
      house1Rules = getDayDates(house1Rules, state.xDays, startDate, endDate);
      break;
    case 'week':
      house1Rules = getWeekDates(house1Rules, 1, state.swapDayOfWeek, startDate, endDate);
      break;
    case 'xweeks':
      house1Rules = getWeekDates(house1Rules, state.xWeeks, state.swapDayOfWeek, startDate, endDate);
      break;
    case 'weekends':
      house1Rules = getWeekendDates(house1Rules, 1, state.weekendStart, state.weekendEnd, startDate, endDate);
      break;
    case 'xweekends':
      house1Rules = getWeekendDates(house1Rules, state.xWeekends, state.weekendStart, state.weekendEnd, startDate, endDate);
      break;
    case 'weekend_weekdays':
      house1Rules = getWeekendWeekDates(house1Rules, state.weekendStart, state.weekendEnd, startDate, endDate);
      break;
    case 'weekend_splitweek':
      if (state.splits[0]) {
        house1Rules = getWeekendSplitDates(
          house1Rules,
          state.startHousehold!,
          state.weekendStart,
          state.weekendEnd,
          state.splits[0],
          startDate,
          endDate
        );
      }
      break;
    case 'alternating':
      house1Rules = getAlternatingDates(house1Rules, household1Id, state.splits, startDate, endDate);
      break;
    case '223':
      house1Rules = get223Dates(house1Rules, state.startHousehold!, startDate, endDate);
      break;
  }

  // Get all dates for first household
  const house1Dates = house1Rules.all();
  const custodyDates: Record<string, CustodyDate> = {};

  // Populate first household's dates
  house1Dates.forEach((d) => {
    if (d.getTime() >= startDate.getTime() && d.getTime() <= endDate.getTime()) {
      const dateStr = dateToString(d);
      let householdId = state.startHousehold || household1Id;

      // Check for manual swap
      if (state.swapDates[dateStr]) {
        householdId = state.swapDates[dateStr];
      }

      const household = households[householdId];
      if (household) {
        custodyDates[dateStr] = {
          date: dateStr,
          householdId,
          household: household.name,
          color: household.color,
          custodyPlanId: state.custodyPlanId || '',
          isSwapped: !!state.swapDates[dateStr],
          startTime: d.getTime(),
          isAllDay: true,
        };
      }
    }
  });

  // Fill in remaining dates with second household
  const secondHouseholdId = hhKeys.find((id) => id !== state.startHousehold) || hhKeys[1];
  const secondHousehold = households[secondHouseholdId];

  if (secondHousehold) {
    const currentDate = new Date(startDate);
    while (currentDate.getTime() <= endDate.getTime()) {
      const dateStr = dateToString(currentDate);

      if (!custodyDates[dateStr]) {
        let householdId = secondHouseholdId;

        // Check for manual swap
        if (state.swapDates[dateStr]) {
          householdId = state.swapDates[dateStr];
        }

        const household = households[householdId];
        if (household) {
          custodyDates[dateStr] = {
            date: dateStr,
            householdId,
            household: household.name,
            color: household.color,
            custodyPlanId: state.custodyPlanId || '',
            isSwapped: !!state.swapDates[dateStr],
            startTime: currentDate.getTime(),
            isAllDay: true,
          };
        }
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return { items: custodyDates, ...ruleInfo };
};

/**
 * Swap custody for a specific date
 */
export const swapCustodyDate = (
  dateStr: string,
  households: Record<string, Household>,
  items: Record<string, CustodyDate>,
  swapDates: Record<string, string>
): { items: Record<string, CustodyDate>; swapDates: Record<string, string> } => {
  const newItems = { ...items };
  const newSwapDates = { ...swapDates };
  const dateObj = newItems[dateStr];

  if (!dateObj) {
    return { items: newItems, swapDates: newSwapDates };
  }

  const hhKeys = Object.keys(households);
  const household1 = households[hhKeys[0]];
  const household2 = households[hhKeys[1]];

  if (!household1 || !household2) {
    return { items: newItems, swapDates: newSwapDates };
  }

  // Toggle between households
  if (dateObj.householdId === hhKeys[0]) {
    dateObj.householdId = hhKeys[1];
    dateObj.household = household2.name;
    dateObj.color = household2.color;
  } else {
    dateObj.householdId = hhKeys[0];
    dateObj.household = household1.name;
    dateObj.color = household1.color;
  }

  // Track or untrack the swap
  if (newSwapDates[dateStr]) {
    delete newSwapDates[dateStr];
    dateObj.isSwapped = false;
  } else {
    newSwapDates[dateStr] = dateObj.householdId;
    dateObj.isSwapped = true;
  }

  newItems[dateStr] = dateObj;

  return { items: newItems, swapDates: newSwapDates };
};

/**
 * Convert CustodyState to CustodyPlan for saving
 */
export const stateToCustodyPlan = (state: CustodyState, familyId: string, userId: string): CustodyPlan => {
  const plan: CustodyPlan = {
    id: state.custodyPlanId || `custody-${Date.now()}`,
    familyId,
    custodyScheme: state.custodyScheme!,
    custodySchemeText: state.custodySchemeText,
    startDate: state.startDate.getTime(),
    endDate: state.endDate.getTime(),
    startHousehold: state.startHousehold!,
    startHouseholdName: state.startHouseholdName,
    swapDates: state.swapDates,
    submitter: userId,
    approved: false,
    versions: state.versions,
  };

  // Add scheme-specific fields
  if (['weekend_splitweek', 'weekend_weekdays', 'weekends', 'xweekends'].includes(state.custodyScheme!)) {
    plan.weekendStart = state.weekendStart;
    plan.weekendEnd = state.weekendEnd;
  }

  if (['week', 'xweeks', '223'].includes(state.custodyScheme!)) {
    plan.swapDayOfWeek = state.swapDayOfWeek;
  }

  if (state.custodyScheme === 'xweeks') {
    plan.xWeeks = state.xWeeks;
  }

  if (state.custodyScheme === 'xdays') {
    plan.xDays = state.xDays;
  }

  if (state.custodyScheme === 'xweekends') {
    plan.xWeekends = state.xWeekends;
  }

  if (['weekend_splitweek', 'alternating'].includes(state.custodyScheme!)) {
    plan.splits = state.splits;
  }

  return plan;
};

/**
 * Convert CustodyPlan to CustodyState for editing
 */
export const custodyPlanToState = (plan: CustodyPlan): Partial<CustodyState> => {
  return {
    custodyScheme: plan.custodyScheme,
    custodySchemeText: plan.custodySchemeText,
    custodyPlanId: plan.id,
    startDate: new Date(plan.startDate),
    endDate: new Date(plan.endDate),
    startHousehold: plan.startHousehold,
    startHouseholdName: plan.startHouseholdName,
    swapDayOfWeek: plan.swapDayOfWeek || 'Sunday',
    swapDayOfMonth: plan.swapDayOfMonth || 1,
    weekendStart: plan.weekendStart || 'Friday',
    weekendEnd: plan.weekendEnd || 'Sunday',
    xDays: plan.xDays || 1,
    xWeeks: plan.xWeeks || 1,
    xMonths: plan.xMonths || 1,
    xWeekends: plan.xWeekends || 1,
    splits: plan.splits || [],
    swapDates: plan.swapDates || {},
    versions: plan.versions || [],
    isParametersComplete: true,
    approved: plan.approved,
  };
};

/**
 * Get initial custody state
 */
export const getInitialCustodyState = (): CustodyState => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(today);
  endDate.setMonth(endDate.getMonth() + 3);

  return {
    custodyScheme: null,
    custodySchemeText: 'Select a schedule...',
    custodyPlanId: null,
    startDate: today,
    endDate,
    lastPlanEndDate: null,
    startHousehold: null,
    startHouseholdName: 'Select...',
    swapDayOfWeek: 'Sunday',
    swapDayOfMonth: 1,
    weekendStart: 'Friday',
    weekendEnd: 'Sunday',
    xDays: 1,
    xWeeks: 1,
    xMonths: 1,
    xWeekends: 1,
    splits: [],
    swapDates: {},
    isParametersComplete: false,
    ruleDescription: '',
    approved: false,
    versions: [],
  };
};
